// Copyright © 2025 Hardcore Engineering Inc.
// Copyright © 2026 TraceX SAS.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.

import { Card, ExportFunc, MasterTag, Tag } from '@hcengineering/card'
import {
  Association,
  Class,
  Client,
  Doc,
  DocumentUpdate,
  Markup,
  ObjQueryType,
  Rank,
  Ref,
  Tx,
  Type
} from '@hcengineering/core'
import { Asset, IntlString, Plugin, plugin, Resource } from '@hcengineering/platform'
import { ToDo } from '@hcengineering/time'
import { AnyComponent } from '@hcengineering/ui'
import { AttributeCategory } from '@hcengineering/view'
import { SelectedExecutionContext } from './types'
import type { ExecutionDiagnostics, ExecutionSource } from './diagnostics'

export type {
  DiagnosticDependency,
  ExecutionReason,
  TransitionDiagnostic,
  ExecutionDiagnostics,
  ExecutionSource
} from './diagnostics'

/**
 * @public
 */
export const processId = 'process' as Plugin

export interface SlotModel {
  slotKind: 'attribute' | 'class' | 'association' | 'process' | 'unknown'
  _class: Ref<Class<Doc>>
  label?: IntlString
  name?: string
  memberOf?: string
}

export interface AttributeSlotModel extends SlotModel {
  slotKind: 'attribute'
  type: Type<any>
  enumValues?: string[]
}

// Process model dscription
export interface Process extends Doc {
  masterTag: Ref<MasterTag | Tag>
  rank: Rank
  name: string
  description: string
  parallelExecutionForbidden?: boolean
  showInHeader?: boolean
  autoStart?: boolean
  automationOnly?: boolean
  context: Record<ContextId, ProcessContext>
  resultType?: Type<any>
  requiredSlots?: Record<string, SlotModel>
  bindings?: Record<string, string>
}

export interface ProcessContext {
  name: string
  _class: Ref<Class<Doc>>
  action: StepId
  index: number
  producer: Ref<Transition>
  isResult?: boolean
  type?: Type<any>
  value: SelectedExecutionContext
}

export type ContextId = string & { __contextId: true }

export interface Trigger extends Doc {
  icon: Asset
  label: IntlString
  requiredParams: string[]
  editor?: AnyComponent
  presenter?: AnyComponent
  checkFunction?: Resource<CheckFunc>
  init: boolean
  auto?: boolean
}

export interface Transition extends Doc {
  process: Ref<Process>
  from: Ref<State> | null
  to: Ref<State>
  actions: Step<Doc>[]
  trigger: Ref<Trigger>
  triggerParams: Record<string, any>
  rank: Rank
}

export interface ExecutionLog extends Doc {
  execution: Ref<Execution>
  card: Ref<Card>
  process: Ref<Process>
  transition?: Ref<Transition>
  action: ExecutionLogAction
}

export enum ExecutionLogAction {
  Started = 'started',
  Transition = 'transition',
  Rollback = 'rollback'
}

export type CheckFunc = (
  client: Client,
  execution: Execution,
  params: Record<string, any>,
  context: Record<string, any>
) => Promise<boolean>

export enum ExecutionStatus {
  Active = 'active',
  Done = 'done',
  Cancelled = 'cancelled'
}

export interface Execution extends Doc {
  process: Ref<Process>
  currentState: Ref<State>
  card: Ref<Card>
  rollback: Tx[][] // just stack
  error?: ExecutionError[] | null
  diagnostics?: ExecutionDiagnostics | null
  context: ExecutionContext
  result?: any
  parentId?: Ref<Execution>
  source?: ExecutionSource
  status: ExecutionStatus
}

export type ExecutionContext = Record<ContextId, any> & { __contextId: true }

export interface ExecutionError {
  error: IntlString
  props: Record<string, any>
  intlProps: Record<string, IntlString>
  transition: Ref<Transition> | undefined
  /** Trigger conditions must be rechecked before retrying the transition. */
  phase?: 'trigger'
  step?: StepId
  method?: Ref<Method<Doc>>
  parameter?: string
}

export interface ProcessToDo extends ToDo {
  execution: Ref<Execution>

  withRollback: boolean

  results?: UserResult[]
  field?: string
  askRequired?: boolean
  group?: string
  completionMode?: 'any' | 'all'
}

export interface ApproveRequest extends ProcessToDo {
  card: Ref<Card>

  approved?: boolean

  reason?: string

  group: string
  field?: string
  actionType?: 'approve' | 'review'
}

export type MethodParams<T extends Doc> = {
  [P in keyof T]?: ObjQueryType<T[P]> | string
} & DocumentUpdate<T> &
  Record<string, any>

export interface State extends Doc {
  process: Ref<Process>
  title: string
  rank: Rank
}

export type StepId = string & { __stepId: true }

export interface Step<T extends Doc> {
  _id: StepId
  context: StepContext | null
  methodId: Ref<Method<T>>
  params: MethodParams<T>
  results?: UserResult[]
}

export interface StepContext {
  _id: ContextId // context id
  _class?: Ref<Class<Doc>> // class of the context
}

export interface SelectionRelation {
  association: Ref<Association>
  direction: 'A' | 'B'
  versions?: 'all' | 'latest' | 'effective'
}

export interface UserResult {
  _id: ContextId // context id
  name: string
  key?: string
  type: Type<any>
  selectionSpace?: string // Space reference or serialized process context
  excludeRelation?: SelectionRelation
}

export interface ProcessCustomEvent extends Doc {
  eventType: string
  execution: Ref<Execution>
  card: Ref<Card>
}

export interface EventButton extends Doc {
  requireAttachments?: boolean
  title: string
  description?: Markup
  eventType: string
  user?: ToDo['user']
  execution: Ref<Execution>
  card: Ref<Card>
}

export interface Method<T extends Doc> extends Doc {
  label: IntlString
  objectClass: Ref<Class<T>>
  requiredParams: string[]
  description?: IntlString
  editor?: AnyComponent
  presenter?: AnyComponent
  createdContext: CreatedContext | null
  defaultParams?: MethodParams<T>
}

export interface CreatedContext {
  _class: Ref<Class<Doc>>
  nameField?: string
}

export interface ProcessFunction extends Doc {
  type: 'transform' | 'reduce' | 'context' | 'convert'
  of: Ref<Class<Doc>>
  to?: Ref<Class<Doc>>
  editor?: AnyComponent
  editorProps?: Record<string, unknown>
  presenter?: AnyComponent
  category: AttributeCategory | undefined
  allowMany?: boolean
  label: IntlString
}

export interface UpdateCriteriaComponent extends Doc {
  category: AttributeCategory
  editor: AnyComponent
  of: Ref<Class<Doc>>
  props: Record<string, any>
}

export * from './dslContext'
export * from './errors'
export type * from './types'
export * from './utils'
export { compileProcessYaml, parseProcessImport, validateProcessYaml, ProcessAuthoringError } from './authoring'
export type { AuthoringNativeDocument, ProcessAuthoringResult } from './authoring'
export type { AuthoringDiagnostic, AuthoringValidation } from './authoring-validation'
export {
  assertTree as assertAuthoringTree,
  object as authoringObject,
  text as authoringText,
  authoringDataEqual
} from './authoring-validation'
export { exportProcessYaml, exportProcessYamlDraft } from './authoring-export'
export {
  bindYamlModelProcess,
  createYamlModelTemplate,
  cloneAuthoringModule,
  isAuthoringSlotBindingCompatible,
  matchesAuthoringSlotType
} from './authoring-bindings'
export {
  exportAuthoringModel,
  getAuthoringCatalog,
  getAuthoringProcessIdentities,
  remapAuthoringReferences,
  validateAuthoringModelOperations
} from './authoring-model'

export default plugin(processId, {
  class: {
    Process: '' as Ref<Class<Process>>,
    Execution: '' as Ref<Class<Execution>>,
    ProcessToDo: '' as Ref<Class<ProcessToDo>>,
    ApproveRequest: '' as Ref<Class<ApproveRequest>>,
    Method: '' as Ref<Class<Method<Doc>>>,
    State: '' as Ref<Class<State>>,
    ProcessFunction: '' as Ref<Class<ProcessFunction>>,
    Transition: '' as Ref<Class<Transition>>,
    Trigger: '' as Ref<Class<Trigger>>,
    ProcessCustomEvent: '' as Ref<Class<ProcessCustomEvent>>,
    ExecutionLog: '' as Ref<Class<ExecutionLog>>,
    UpdateCriteriaComponent: '' as Ref<Class<UpdateCriteriaComponent>>,
    EventButton: '' as Ref<Class<EventButton>>
  },
  method: {
    RunSubProcess: '' as Ref<Method<Process>>,
    CancelSubProcess: '' as Ref<Method<Process>>,
    CreateAction: '' as Ref<Method<EventButton>>,
    RequestAttachments: '' as Ref<Method<EventButton>>,
    SetContext: '' as Ref<Method<Doc>>,
    UpdateContext: '' as Ref<Method<Doc>>,
    CancellAction: '' as Ref<Method<EventButton>>,
    CreateToDo: '' as Ref<Method<ProcessToDo>>,
    CloseToDo: '' as Ref<Method<ProcessToDo>>,
    UpdateCard: '' as Ref<Method<Card>>,
    CreateCard: '' as Ref<Method<Card>>,
    AddRelation: '' as Ref<Method<Association>>,
    RemoveRelation: '' as Ref<Method<Association>>,
    AddTag: '' as Ref<Method<Tag>>,
    RemoveTag: '' as Ref<Method<Tag>>,
    RequestApproval: '' as Ref<Method<ApproveRequest>>,
    CancelToDo: '' as Ref<Method<ProcessToDo>>,
    LockCard: '' as Ref<Method<Card>>,
    LockSection: '' as Ref<Method<Card>>,
    UnlockCard: '' as Ref<Method<Card>>,
    UnlockSection: '' as Ref<Method<Card>>,
    LockField: '' as Ref<Method<Card>>,
    UnlockField: '' as Ref<Method<Card>>,
    MakeVersionEffective: '' as Ref<Method<Card>>,
    CreateNewVersion: '' as Ref<Method<Card>>,
    DisableVersionCreation: '' as Ref<Method<Card>>,
    EnableVersionCreation: '' as Ref<Method<Card>>
  },
  trigger: {
    OnCardUpdate: '' as Ref<Trigger>, // in fact WhenCardMatches, should migrate in future
    WhenFieldChanges: '' as Ref<Trigger>,
    WhenRequiredFieldsFilled: '' as Ref<Trigger>,
    OnSubProcessesDone: '' as Ref<Trigger>,
    OnSubProcessMatch: '' as Ref<Trigger>,
    OnToDoClose: '' as Ref<Trigger>,
    OnToDoRemove: '' as Ref<Trigger>,
    OnExecutionStart: '' as Ref<Trigger>,
    OnNewVersion: '' as Ref<Trigger>,
    OnVersionEffective: '' as Ref<Trigger>,
    OnVersionIneffective: '' as Ref<Trigger>,
    OnExecutionContinue: '' as Ref<Trigger>,
    OnTime: '' as Ref<Trigger>,
    OnEvent: '' as Ref<Trigger>,
    WhenRelationChanges: '' as Ref<Trigger>,
    OnApproveRequestApproved: '' as Ref<Trigger>,
    OnApproveRequestRejected: '' as Ref<Trigger>
  },
  triggerCheck: {
    ToDo: '' as Resource<CheckFunc>,
    MatchCheck: '' as Resource<CheckFunc>,
    FieldChangedCheck: '' as Resource<CheckFunc>,
    RequiredFieldsFilledCheck: '' as Resource<CheckFunc>,
    SubProcessesDoneCheck: '' as Resource<CheckFunc>,
    SubProcessMatchCheck: '' as Resource<CheckFunc>,
    Time: '' as Resource<CheckFunc>,
    OnEventCheck: '' as Resource<CheckFunc>,
    RelationChangedCheck: '' as Resource<CheckFunc>,
    ApproveRequestApproved: '' as Resource<CheckFunc>,
    ApproveRequestRejected: '' as Resource<CheckFunc>
  },
  string: {
    Processes: '' as IntlString,
    RequiredSlots: '' as IntlString,
    Import: '' as IntlString,
    DiagnosticTitle: '' as IntlString,
    DiagnosticLastChecked: '' as IntlString,
    DiagnosticNotChecked: '' as IntlString,
    DiagnosticEvent: '' as IntlString,
    DiagnosticCondition: '' as IntlString,
    DiagnosticNoDetails: '' as IntlString,
    DiagnosticTasks: '' as IntlString,
    DiagnosticTasksMissing: '' as IntlString,
    DiagnosticResultMismatch: '' as IntlString,
    DiagnosticSubprocesses: '' as IntlString,
    DiagnosticSubprocessesInactive: '' as IntlString,
    DiagnosticSubprocessesMissing: '' as IntlString,
    DiagnosticApprovals: '' as IntlString,
    DiagnosticRejections: '' as IntlString,
    DiagnosticRequiredFields: '' as IntlString,
    DiagnosticMissingValue: '' as IntlString,
    DiagnosticTime: '' as IntlString,
    DiagnosticAll: '' as IntlString,
    DiagnosticAny: '' as IntlString,
    DiagnosticNone: '' as IntlString,
    DiagnosticUnavailable: '' as IntlString,
    DiagnosticMore: '' as IntlString,
    DiagnosticTruncated: '' as IntlString,
    DiagnosticCycle: '' as IntlString,
    DiagnosticDepth: '' as IntlString,
    DiagnosticCurrentState: '' as IntlString,
    DiagnosticStep: '' as IntlString,
    DiagnosticParameter: '' as IntlString,
    DiagnosticRejected: '' as IntlString,
    DiagnosticPending: '' as IntlString,
    DiagnosticMatched: '' as IntlString,
    DiagnosticError: '' as IntlString,
    Method: '' as IntlString,
    Execution: '' as IntlString,
    Process: '' as IntlString,
    Step: '' as IntlString,
    Error: '' as IntlString,
    From: '' as IntlString,
    To: '' as IntlString,
    Trigger: '' as IntlString,
    Actions: '' as IntlString,
    ProcessRunned: '' as IntlString,
    ProcessStateChanged: '' as IntlString,
    ProcessFinished: '' as IntlString,
    SyncWithField: '' as IntlString,
    ActionType: '' as IntlString,
    ApproveAction: '' as IntlString,
    ReviewAction: '' as IntlString,
    Review: '' as IntlString
  },
  error: {
    TagHasSubtags: '' as IntlString,
    MethodNotFound: '' as IntlString,
    InternalServerError: '' as IntlString,
    EmptyRelatedObjectValue: '' as IntlString,
    RelatedObjectNotFound: '' as IntlString,
    RelationNotExists: '' as IntlString,
    EmptyAttributeContextValue: '' as IntlString,
    ObjectNotFound: '' as IntlString,
    AttributeNotExists: '' as IntlString,
    UserRequestedValueNotProvided: '' as IntlString,
    ResultNotProvided: '' as IntlString,
    EmptyFunctionResult: '' as IntlString,
    ContextValueNotProvided: '' as IntlString,
    RequiredParamsNotProvided: '' as IntlString,
    TooDeepTransitionRecursion: '' as IntlString,
    ToDoAlreadyCompleted: '' as IntlString,
    GuestWithoutCardAccess: '' as IntlString
  },
  icon: {
    Process: '' as Asset,
    Steps: '' as Asset,
    States: '' as Asset,
    ToDo: '' as Asset,
    WhenCardMatches: '' as Asset,
    WhenFieldChanges: '' as Asset,
    WaitSubprocesses: '' as Asset,
    ToDoRemove: '' as Asset,
    Start: '' as Asset,
    Time: '' as Asset,
    OnEvent: '' as Asset
  },
  function: {
    AllMatchValue: '' as Ref<ProcessFunction>,
    FirstMatchValue: '' as Ref<ProcessFunction>,
    Filter: '' as Ref<ProcessFunction>,
    ArrayLength: '' as Ref<ProcessFunction>,
    RelationCount: '' as Ref<ProcessFunction>,
    FindObjects: '' as Ref<ProcessFunction>,
    FirstValue: '' as Ref<ProcessFunction>,
    LastValue: '' as Ref<ProcessFunction>,
    Random: '' as Ref<ProcessFunction>,
    UpperCase: '' as Ref<ProcessFunction>,
    LowerCase: '' as Ref<ProcessFunction>,
    Trim: '' as Ref<ProcessFunction>,
    Prepend: '' as Ref<ProcessFunction>,
    Append: '' as Ref<ProcessFunction>,
    PrependMarkup: '' as Ref<ProcessFunction>,
    AppendMarkup: '' as Ref<ProcessFunction>,
    Replace: '' as Ref<ProcessFunction>,
    ReplaceAll: '' as Ref<ProcessFunction>,
    Split: '' as Ref<ProcessFunction>,
    Cut: '' as Ref<ProcessFunction>,
    Add: '' as Ref<ProcessFunction>,
    Subtract: '' as Ref<ProcessFunction>,
    Multiply: '' as Ref<ProcessFunction>,
    Divide: '' as Ref<ProcessFunction>,
    Modulo: '' as Ref<ProcessFunction>,
    Power: '' as Ref<ProcessFunction>,
    Sqrt: '' as Ref<ProcessFunction>,
    Round: '' as Ref<ProcessFunction>,
    Absolute: '' as Ref<ProcessFunction>,
    Ceil: '' as Ref<ProcessFunction>,
    Floor: '' as Ref<ProcessFunction>,
    Min: '' as Ref<ProcessFunction>,
    Max: '' as Ref<ProcessFunction>,
    Offset: '' as Ref<ProcessFunction>,
    FirstWorkingDayAfter: '' as Ref<ProcessFunction>,
    RoleContext: '' as Ref<ProcessFunction>,
    All: '' as Ref<ProcessFunction>,
    Insert: '' as Ref<ProcessFunction>,
    Remove: '' as Ref<ProcessFunction>,
    RemoveFirst: '' as Ref<ProcessFunction>,
    RemoveLast: '' as Ref<ProcessFunction>,
    CurrentEmployee: '' as Ref<ProcessFunction>,
    CurrentUser: '' as Ref<ProcessFunction>,
    ExecutionStarted: '' as Ref<ProcessFunction>,
    ExecutionEmployeeInitiator: '' as Ref<ProcessFunction>,
    ExecutionInitiator: '' as Ref<ProcessFunction>,
    EmptyValue: '' as Ref<ProcessFunction>,
    EmptyArray: '' as Ref<ProcessFunction>,
    CurrentDate: '' as Ref<ProcessFunction>,
    StringFromIdentifier: '' as Ref<ProcessFunction>,
    StringFromNumber: '' as Ref<ProcessFunction>,
    StringFromDate: '' as Ref<ProcessFunction>,
    StringFromMarkup: '' as Ref<ProcessFunction>,
    MarkupFromString: '' as Ref<ProcessFunction>,
    StringFromBoolean: '' as Ref<ProcessFunction>,
    NumberFromDate: '' as Ref<ProcessFunction>,
    DateFromNumber: '' as Ref<ProcessFunction>,
    NumberFromString: '' as Ref<ProcessFunction>,
    DateFromString: '' as Ref<ProcessFunction>,
    YearFromDate: '' as Ref<ProcessFunction>,
    MonthFromDate: '' as Ref<ProcessFunction>,
    DayFromDate: '' as Ref<ProcessFunction>,
    StringFromEnum: '' as Ref<ProcessFunction>,
    EnumFromString: '' as Ref<ProcessFunction>,
    DateDifference: '' as Ref<ProcessFunction>,
    TableFromRelation: '' as Ref<ProcessFunction>,
    ExportProcess: '' as Resource<ExportFunc>,
    CheckProcessSectionVisibility: '' as Resource<(doc: Card) => Promise<boolean>>
  }
})
